import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MapPin, Building2, Smartphone, Users, CheckCircle, ArrowRight,
  Plus, Trash2, ChevronRight, ChevronLeft, Save, AlertCircle, Key, Settings, Utensils
} from 'lucide-react';
import { apiErrorMessage } from './utils/apiError';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const ENABLE_ONBOARDING_MVP = String(process.env.REACT_APP_ENABLE_ONBOARDING_MVP || '').toLowerCase() === 'true';

const Spinner = () => (
  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
);

export const OnboardingWizard = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [orgId, setOrgId] = useState(null);
  const [status, setStatus] = useState(null);
  // Every write action below funnels its error here instead of failing
  // silently. Previously a non-2xx response (quota block, validation error,
  // server error) left the button looking dead with zero feedback — the
  // exact bug reported live: POST /api/platform/locations 403 on first use.
  const [actionError, setActionError] = useState(null);

  // Form States
  const [locations, setLocations] = useState([]);
  const [newLocation, setNewLocation] = useState('');
  
  const [customDepts, setCustomDepts] = useState([]);
  const [newDept, setNewDept] = useState('');

  const [devices, setDevices] = useState([]);
  const [newDevice, setNewDevice] = useState({ name: '', type: 'pos_terminal', identifier: '' });

  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState(null);

  const [moduleConfig, setModuleConfig] = useState({});
  const [savingModules, setSavingModules] = useState(false);

  const [invites, setInvites] = useState([]);
  const [newInvite, setNewInvite] = useState({ email: '', role: 'org_admin' });
  const [ingestEmail, setIngestEmail] = useState('');
  const [entityRows, setEntityRows] = useState([
    { entity_name: '', company_no: '', vat_no: '', site_name: '' },
  ]);
  const [groupName, setGroupName] = useState('');
  const [hierarchyBusy, setHierarchyBusy] = useState(false);
  const [magicLinkResult, setMagicLinkResult] = useState(null);

  // Menu Configuration
  const [menuConfig, setMenuConfig] = useState(null);
  const [customFields, setCustomFields] = useState([]);
  const [savingMenuConfig, setSavingMenuConfig] = useState(false);

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
          navigate('/login');
          return;
      }

      // 1. Get Status & Context
      const res = await fetch(`${API_URL}/api/platform/onboarding/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 403) {
          alert("Access denied. Please contact your administrator.");
          navigate('/');
          return;
      }
      const data = await res.json();
      setStatus(data);
      setOrgId(data.organization_id);
      if (data.ingest_email) setIngestEmail(data.ingest_email);
      if (data.organization_name) setGroupName(data.organization_name);

      // Load existing data
      if (data.organization_id) {
          await loadData(token, data.organization_id);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async (token, oid) => {
      const headers = { Authorization: `Bearer ${token}`, 'X-Organization-Id': oid };

      // Locations
      const locRes = await fetch(`${API_URL}/api/platform/locations?organization_id=${oid}`, { headers });
      if (locRes.ok) setLocations(await locRes.json());

      // Depts & Modules (via module config)
      const cfgRes = await fetch(`${API_URL}/api/platform/module-config?organization_id=${oid}`, { headers });
      if (cfgRes.ok) {
          const cfg = await cfgRes.json();
          setCustomDepts(cfg.policies?.custom_departments || []);
          setModuleConfig(cfg.modules_enabled || {});
      }

      // Devices
      const devRes = await fetch(`${API_URL}/api/platform/devices?organization_id=${oid}`, { headers });
      if (devRes.ok) setDevices(await devRes.json());

      // API Keys
      const keyRes = await fetch(`${API_URL}/api/auth/api-keys`, { headers });
      if (keyRes.ok) setApiKeys(await keyRes.json());
  };

  // --- Actions ---

  const addLocation = async () => {
      if (!newLocation.trim()) return;
      setActionError(null);
      try {
          const token = localStorage.getItem('token');
          const res = await fetch(`${API_URL}/api/platform/locations`, {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                  'X-Organization-Id': orgId
              },
              body: JSON.stringify({
                  organization_id: orgId,
                  name: newLocation,
                  timezone: 'UTC'
              })
          });
          if (res.ok) {
              const loc = await res.json();
              setLocations([...locations, loc]);
              setNewLocation('');
          } else {
              const err = await res.json().catch(() => ({}));
              setActionError(apiErrorMessage(err.detail, `Could not add location (${res.status})`));
          }
      } catch (e) {
          console.error(e);
          setActionError('Network error — could not reach the server.');
      }
  };

  const saveDepartments = async () => {
      setActionError(null);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/platform/departments`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                'X-Organization-Id': orgId
            },
            body: JSON.stringify({ custom_departments: customDepts })
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            setActionError(apiErrorMessage(err.detail, `Could not save departments (${res.status})`));
        }
      } catch (e) {
          console.error(e);
          setActionError('Network error — could not reach the server.');
      }
  };

  const addDevice = async () => {
      if (!newDevice.name || !newDevice.identifier) return;
      setActionError(null);
      try {
          const token = localStorage.getItem('token');
          const res = await fetch(`${API_URL}/api/platform/devices`, {
              method: 'POST',
              headers: { 
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                  'X-Organization-Id': orgId
              },
              body: JSON.stringify({
                  organization_id: orgId,
                  location_id: locations[0]?.id, // Default to first location
                  device_type: newDevice.type,
                  name: newDevice.name,
                  identifier: newDevice.identifier
              })
          });
          if (res.ok) {
              const dev = await res.json();
              setDevices([...devices, dev]);
              setNewDevice({ name: '', type: 'pos_terminal', identifier: '' });
          } else {
              const err = await res.json().catch(() => ({}));
              setActionError(apiErrorMessage(err.detail, `Could not add device (${res.status})`));
          }
      } catch (e) {
          console.error(e);
          setActionError('Network error — could not reach the server.');
      }
  };

  const createApiKey = async () => {
      if (!newKeyName.trim()) return;
      setActionError(null);
      try {
          const token = localStorage.getItem('token');
          // create_api_key declares name/organization_id/permissions as query
          // parameters, not a body model. Sending JSON here 422'd even once the
          // path was corrected; repeated `permissions` params map to List[str].
          const params = new URLSearchParams({ name: newKeyName });
          if (orgId) params.set('organization_id', orgId);
          for (const perm of ['read', 'write']) params.append('permissions', perm);
          const res = await fetch(`${API_URL}/api/auth/api-keys?${params.toString()}`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) {
              const key = await res.json();
              setApiKeys([...apiKeys, key]);
              setCreatedKey(key.key); // Show the secret key once
              setNewKeyName('');
          } else {
              const err = await res.json().catch(() => ({}));
              setActionError(apiErrorMessage(err.detail, `Could not create API key (${res.status})`));
          }
      } catch (e) {
          console.error(e);
          setActionError('Network error — could not reach the server.');
      }
  };

  const toggleModule = (key) => {
      setModuleConfig(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const saveMenuConfig = async () => {
      setSavingMenuConfig(true);
      setActionError(null);
      try {
          const token = localStorage.getItem('token');
          const locationId = locations[0]?.id || null;
          
          const configData = {
              organization_id: orgId,
              location_id: locationId,
              name: menuConfig?.name || 'Default Menu Configuration',
              description: menuConfig?.description || '',
              is_active: true,
              custom_field_definitions: customFields,
              pricing_rules: {},
              availability_schedules: {}
          };

          const res = await fetch(`${API_URL}/api/config/menu`, {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                  'X-Organization-Id': orgId
              },
              body: JSON.stringify(configData)
          });
          if (!res.ok) {
              const err = await res.json().catch(() => ({}));
              setActionError(apiErrorMessage(err.detail, `Could not save menu configuration (${res.status})`));
          }
      } catch (e) {
          console.error(e);
          setActionError('Network error — could not reach the server.');
      }
      finally { setSavingMenuConfig(false); }
  };

  const saveModules = async () => {
      setSavingModules(true);
      setActionError(null);
      try {
          const token = localStorage.getItem('token');
          const res = await fetch(`${API_URL}/api/platform/module-config`, {
              method: 'PUT',
              headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                  'X-Organization-Id': orgId
              },
              body: JSON.stringify({
                  organization_id: orgId,
                  modules_enabled: moduleConfig
              })
          });
          if (!res.ok) {
              const err = await res.json().catch(() => ({}));
              setActionError(apiErrorMessage(err.detail, `Could not save modules (${res.status})`));
          }
      } catch (e) {
          console.error(e);
          setActionError('Network error — could not reach the server.');
      }
      finally { setSavingModules(false); }
  };

  const sendInvite = async () => {
      if (!newInvite.email) return;
      setActionError(null);
      try {
          const token = localStorage.getItem('token');
          const endpoint = ENABLE_ONBOARDING_MVP
              ? `${API_URL}/api/onboarding/invites`
              : `${API_URL}/api/platform/memberships/invite`;
          const body = ENABLE_ONBOARDING_MVP
              ? { email: newInvite.email, role: newInvite.role || 'org_admin' }
              : {
                  organization_id: orgId,
                  email: newInvite.email,
                  roles: [newInvite.role],
                  departments: [],
                  location_ids: []
              };
          const res = await fetch(endpoint, {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                  'X-Organization-Id': orgId
              },
              body: JSON.stringify(body)
          });
          if (res.ok) {
              const inv = await res.json();
              setInvites([...invites, inv]);
              if (inv.magic_link) setMagicLinkResult(inv.magic_link);
              setNewInvite({ email: '', role: ENABLE_ONBOARDING_MVP ? 'org_admin' : 'staff' });
              alert(inv.magic_link ? 'Magic link created — copy it for your finance lead.' : 'Invite sent!');
          } else {
              const err = await res.json().catch(() => ({}));
              setActionError(apiErrorMessage(err.detail, `Could not send invite (${res.status})`));
          }
      } catch (e) {
          console.error(e);
          setActionError('Network error — could not reach the server.');
      }
  };

  const provisionCompanies = async () => {
      if (!groupName.trim()) return;
      const entities = [];
      for (const row of entityRows) {
          if (!row.entity_name.trim()) continue;
          let ent = entities.find(e => e.entity_name === row.entity_name.trim());
          if (!ent) {
              ent = {
                  entity_name: row.entity_name.trim(),
                  company_no: row.company_no || null,
                  vat_no: row.vat_no || null,
                  sites: [],
              };
              entities.push(ent);
          }
          if (row.site_name.trim()) {
              ent.sites.push({ name: row.site_name.trim() });
          }
      }
      if (!entities.length) return;
      setHierarchyBusy(true);
      try {
          const token = localStorage.getItem('token');
          const res = await fetch(`${API_URL}/api/onboarding/groups`, {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ group_name: groupName.trim(), entities }),
          });
          if (res.ok) {
              const data = await res.json();
              setOrgId(data.group.id);
              // Canonical key only. This was the sole writer of the legacy
              // `organization_id`, which several components then read while
              // login wrote `current_organization_id` — defect D1.
              localStorage.setItem('current_organization_id', data.group.id);
              setIngestEmail(data.group.ingest_email || '');
              setLocations(data.sites || []);
              alert('Companies & sites created. Forward invoices to your ingest address.');
          } else {
              const err = await res.json().catch(() => ({}));
              alert(err.detail || 'Failed to create hierarchy');
          }
      } catch (e) { console.error(e); }
      finally { setHierarchyBusy(false); }
  };

  const uploadCompaniesCsv = async (file) => {
      if (!file || !groupName.trim()) return;
      setHierarchyBusy(true);
      try {
          const token = localStorage.getItem('token');
          const fd = new FormData();
          fd.append('file', file);
          const res = await fetch(
              `${API_URL}/api/onboarding/groups/csv?group_name=${encodeURIComponent(groupName.trim())}`,
              {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}` },
                  body: fd,
              }
          );
          if (res.ok) {
              const data = await res.json();
              setOrgId(data.group.id);
              setIngestEmail(data.group.ingest_email || '');
              setLocations(data.sites || []);
          } else {
              const err = await res.json().catch(() => ({}));
              alert(err.detail || 'CSV import failed');
          }
      } catch (e) { console.error(e); }
      finally { setHierarchyBusy(false); }
  };

  const completeOnboarding = async () => {
      try {
          const token = localStorage.getItem('token');
          if (token && orgId) {
              await fetch(`${API_URL}/api/platform/onboarding/complete?organization_id=${encodeURIComponent(orgId)}`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}` },
              });
          }
      } catch (e) {
          console.error(e);
      }
      navigate('/dashboard');
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Spinner /></div>;

  const steps = [
      { id: 1, label: 'Locations', icon: MapPin },
      { id: 2, label: 'Departments', icon: Building2 },
      { id: 3, label: 'Menu Config', icon: Utensils },
      { id: 4, label: 'Devices', icon: Smartphone },
      { id: 5, label: 'API Keys', icon: Key },
      { id: 6, label: 'Modules', icon: Settings },
      { id: 7, label: 'Team', icon: Users },
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Progress */}
        <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Setup Your Restaurant</h1>
            <p className="text-gray-600 mb-6">Let's get your organization ready for AI automation.</p>
            
            <div className="flex items-center justify-between relative">
                <div className="absolute left-0 top-1/2 w-full h-1 bg-gray-200 -z-10"></div>
                {steps.map((s) => (
                    <div key={s.id} className={`flex flex-col items-center bg-gray-50 px-2 ${step >= s.id ? 'text-primary-600' : 'text-gray-400'}`}>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-colors ${step >= s.id ? 'bg-primary-100 border-2 border-primary-500' : 'bg-white border-2 border-gray-200'}`}>
                            <s.icon className="w-5 h-5" />
                        </div>
                        <span className="text-xs font-medium hidden sm:block">{s.label}</span>
                    </div>
                ))}
            </div>
        </div>

        {actionError && (
            <div className="mb-6 flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{actionError}</span>
                </div>
                <button
                    type="button"
                    onClick={() => setActionError(null)}
                    className="text-red-500 hover:text-red-700 font-medium flex-shrink-0"
                    aria-label="Dismiss error"
                >
                    ×
                </button>
            </div>
        )}

        {/* Content Card */}
        <div className="bg-white rounded-2xl shadow-sm border p-8 min-h-[400px] relative">
            
            {/* Step 1: Locations / Companies & sites (MVP) */}
            {step === 1 && ENABLE_ONBOARDING_MVP && (
                <div className="space-y-6">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Your companies &amp; sites</h2>
                        <p className="text-gray-500">Create your group, legal entities, and restaurant sites.</p>
                    </div>
                    <input
                        type="text"
                        className="w-full rounded-lg border-gray-300 border px-3 py-2"
                        placeholder="Group name"
                        value={groupName}
                        onChange={e => setGroupName(e.target.value)}
                    />
                    <div className="space-y-3">
                        {entityRows.map((row, i) => (
                            <div key={i} className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                                <input className="rounded-lg border px-3 py-2" placeholder="Entity name"
                                    value={row.entity_name}
                                    onChange={e => {
                                        const next = [...entityRows];
                                        next[i] = { ...next[i], entity_name: e.target.value };
                                        setEntityRows(next);
                                    }} />
                                <input className="rounded-lg border px-3 py-2" placeholder="Company no"
                                    value={row.company_no}
                                    onChange={e => {
                                        const next = [...entityRows];
                                        next[i] = { ...next[i], company_no: e.target.value };
                                        setEntityRows(next);
                                    }} />
                                <input className="rounded-lg border px-3 py-2" placeholder="VAT no"
                                    value={row.vat_no}
                                    onChange={e => {
                                        const next = [...entityRows];
                                        next[i] = { ...next[i], vat_no: e.target.value };
                                        setEntityRows(next);
                                    }} />
                                <input className="rounded-lg border px-3 py-2" placeholder="Site name"
                                    value={row.site_name}
                                    onChange={e => {
                                        const next = [...entityRows];
                                        next[i] = { ...next[i], site_name: e.target.value };
                                        setEntityRows(next);
                                    }} />
                            </div>
                        ))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={() => setEntityRows([...entityRows, { entity_name: '', company_no: '', vat_no: '', site_name: '' }])}
                            className="border px-3 py-2 rounded-lg text-sm">Add row</button>
                        <button type="button" onClick={provisionCompanies} disabled={hierarchyBusy}
                            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50">
                            {hierarchyBusy ? 'Saving…' : 'Create structure'}
                        </button>
                        <label className="border px-3 py-2 rounded-lg text-sm cursor-pointer">
                            Upload CSV
                            <input type="file" accept=".csv,text/csv" className="hidden"
                                onChange={e => uploadCompaniesCsv(e.target.files?.[0])} />
                        </label>
                    </div>
                    {ingestEmail && (
                        <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-lg text-sm">
                            <p className="font-medium text-emerald-800">Forward invoices to:</p>
                            <p className="font-mono text-emerald-900 break-all">{ingestEmail}</p>
                            <p className="text-emerald-700 mt-1">Forward your last 3 months of supplier invoices tonight — see category spend tomorrow.</p>
                        </div>
                    )}
                </div>
            )}

            {step === 1 && !ENABLE_ONBOARDING_MVP && (
                <div className="space-y-6">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Add Locations</h2>
                        <p className="text-gray-500">Where are your restaurants located?</p>
                    </div>

                    <div className="space-y-3">
                        {locations.map(loc => (
                            <div key={loc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                                <span className="font-medium">{loc.name}</span>
                                <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded border">Active</span>
                            </div>
                        ))}
                    </div>

                    <div className="flex gap-2">
                        <input 
                            type="text" 
                            className="flex-1 rounded-lg border-gray-300 border px-3 py-2"
                            placeholder="e.g. Downtown Branch"
                            value={newLocation}
                            onChange={e => setNewLocation(e.target.value)}
                        />
                        <button onClick={addLocation} className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700">
                            Add
                        </button>
                    </div>
                    
                    {locations.length === 0 && (
                        <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg text-sm">
                            <AlertCircle className="w-4 h-4" /> At least one location is recommended.
                        </div>
                    )}
                </div>
            )}

            {/* Step 2: Departments */}
            {step === 2 && (
                <div className="space-y-6">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Custom Departments</h2>
                        <p className="text-gray-500">Add specific departments beyond the standard ones (Kitchen, Bar, FOH).</p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        {['Kitchen', 'Bar', 'FOH', 'HR', 'Finance', 'Ops', 'Admin'].map(d => (
                             <span key={d} className="px-3 py-1 bg-gray-100 text-gray-500 rounded-full text-sm border cursor-not-allowed">
                                 {d} (Default)
                             </span>
                        ))}
                        {customDepts.map((d, i) => (
                             <span key={i} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm border border-blue-200 flex items-center gap-1">
                                 {d}
                                 <button onClick={() => {
                                     const next = customDepts.filter((_, idx) => idx !== i);
                                     setCustomDepts(next);
                                 }}><Trash2 className="w-3 h-3" /></button>
                             </span>
                        ))}
                    </div>

                    <div className="flex gap-2">
                        <input 
                            type="text" 
                            className="flex-1 rounded-lg border-gray-300 border px-3 py-2"
                            placeholder="e.g. Marketing, Delivery"
                            value={newDept}
                            onChange={e => setNewDept(e.target.value)}
                        />
                        <button onClick={() => {
                            if (newDept.trim() && !customDepts.includes(newDept.trim())) {
                                setCustomDepts([...customDepts, newDept.trim()]);
                                setNewDept('');
                            }
                        }} className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700">
                            Add
                        </button>
                    </div>
                </div>
            )}

            {/* Step 3: Menu Configuration */}
            {step === 3 && (
                <div className="space-y-6">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Menu Configuration</h2>
                        <p className="text-gray-500">Configure custom fields for your menu items (e.g., spice level, cooking instructions).</p>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Menu Configuration Name</label>
                            <input aria-label="Menu Configuration Name"
                                type="text"
                                value={menuConfig?.name || ''}
                                onChange={(e) => setMenuConfig({ ...menuConfig, name: e.target.value })}
                                className="w-full rounded-lg border-gray-300 border px-3 py-2"
                                placeholder="e.g. Main Menu Configuration"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Custom Fields</label>
                            <p className="text-xs text-gray-500 mb-3">Add custom fields that will appear on all menu items (e.g., SKU, spice level, dietary tags).</p>
                            
                            {customFields.length > 0 && (
                                <div className="space-y-2 mb-4">
                                    {customFields.map((field, idx) => (
                                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                                            <div>
                                                <span className="font-medium">{field.label || field.name}</span>
                                                <span className="text-xs text-gray-500 ml-2">({field.field_type})</span>
                                            </div>
                                            <button
                                                onClick={() => setCustomFields(customFields.filter((_, i) => i !== idx))}
                                                className="text-red-600 hover:text-red-800"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <button
                                onClick={() => {
                                    const newField = {
                                        id: `field_${Date.now()}`,
                                        name: `custom_field_${customFields.length + 1}`,
                                        label: `Custom Field ${customFields.length + 1}`,
                                        field_type: 'text',
                                        is_required: false
                                    };
                                    setCustomFields([...customFields, newField]);
                                }}
                                className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-2"
                            >
                                <Plus className="w-4 h-4" /> Add Custom Field
                            </button>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <p className="text-sm text-blue-800">
                                <strong>Tip:</strong> You can configure detailed menu settings and add menu items after completing onboarding. This step sets up the basic structure.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Step 4: Devices */}
            {step === 4 && (
                <div className="space-y-6">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Register Devices</h2>
                        <p className="text-gray-500">Connect your POS, Tablets, or Phone Lines.</p>
                    </div>

                    <div className="space-y-3">
                        {devices.map(dev => (
                            <div key={dev.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                                <div>
                                    <div className="font-medium">{dev.name}</div>
                                    <div className="text-xs text-gray-500">{dev.device_type} • {dev.identifier}</div>
                                </div>
                                <CheckCircle className="w-4 h-4 text-green-500" />
                            </div>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <input 
                            type="text" 
                            className="rounded-lg border-gray-300 border px-3 py-2"
                            placeholder="Device Name (e.g. Main POS)"
                            value={newDevice.name}
                            onChange={e => setNewDevice({...newDevice, name: e.target.value})}
                        />
                        <select 
                            className="rounded-lg border-gray-300 border px-3 py-2"
                            value={newDevice.type}
                            onChange={e => setNewDevice({...newDevice, type: e.target.value})}
                        >
                            <option value="pos_terminal">POS Terminal</option>
                            <option value="kds_screen">KDS Screen</option>
                            <option value="tablet">Tablet</option>
                            <option value="phone_line">Phone Line</option>
                        </select>
                        <input 
                            type="text" 
                            className="rounded-lg border-gray-300 border px-3 py-2"
                            placeholder="Identifier (IP/Mac/Phone)"
                            value={newDevice.identifier}
                            onChange={e => setNewDevice({...newDevice, identifier: e.target.value})}
                        />
                    </div>
                    <button onClick={addDevice} className="w-full bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center justify-center gap-2">
                        <Plus className="w-4 h-4" /> Add Device
                    </button>
                </div>
            )}

            {/* Step 5: API Keys */}
            {step === 5 && (
                <div className="space-y-6">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">API Keys</h2>
                        <p className="text-gray-500">Create keys for your devices or external integrations.</p>
                    </div>

                    {createdKey && (
                        <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                            <div className="font-medium text-green-800 mb-1">Key Created Successfully!</div>
                            <div className="text-sm text-green-700 break-all font-mono bg-white p-2 rounded border border-green-100">
                                {createdKey}
                            </div>
                            <div className="text-xs text-green-600 mt-1">Copy this now. You won't see it again.</div>
                            <button onClick={() => setCreatedKey(null)} className="text-sm text-green-800 underline mt-2">Dismiss</button>
                        </div>
                    )}

                    <div className="space-y-3">
                        {apiKeys.map(k => (
                            <div key={k.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                                <div>
                                    <div className="font-medium">{k.name}</div>
                                    <div className="text-xs text-gray-500 font-mono">Prefix: {k.key_prefix} • Created: {new Date(k.created_at).toLocaleDateString()}</div>
                                </div>
                                <span className={`text-xs px-2 py-1 rounded border ${k.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                    {k.is_active ? 'Active' : 'Revoked'}
                                </span>
                            </div>
                        ))}
                    </div>

                    <div className="flex gap-2">
                        <input 
                            type="text" 
                            className="flex-1 rounded-lg border-gray-300 border px-3 py-2"
                            placeholder="Key Name (e.g. Kitchen Display System)"
                            value={newKeyName}
                            onChange={e => setNewKeyName(e.target.value)}
                        />
                        <button onClick={createApiKey} className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700">
                            Generate Key
                        </button>
                    </div>
                </div>
            )}

            {/* Step 6: Modules */}
            {step === 6 && (
                <div className="space-y-6">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Enable Modules</h2>
                        <p className="text-gray-500">Choose which features to activate for your organization.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[
                            { key: 'events', label: 'Event Planning', desc: 'Weddings, parties, catering' },
                            { key: 'inventory', label: 'Inventory Management', desc: 'Stock tracking, waste reduction' },
                            { key: 'scheduling', label: 'Staff Scheduling', desc: 'Shifts, labor costs' },
                            { key: 'implementations', label: 'Implementation Tracker', desc: 'Track AI rollout progress' },
                            { key: 'proposals', label: 'Proposal Generator', desc: 'Create sales proposals' },
                            { key: 'calculator', label: 'ROI Calculator', desc: 'Cost savings analysis' },
                        ].map(m => (
                            <div key={m.key} className={`border rounded-lg p-4 cursor-pointer transition ${moduleConfig[m.key] !== false ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'}`} onClick={() => toggleModule(m.key)}>
                                <div className="flex items-center justify-between mb-1">
                                    <span className="font-semibold text-gray-900">{m.label}</span>
                                    <div className={`w-10 h-5 rounded-full relative transition-colors ${moduleConfig[m.key] !== false ? 'bg-primary-500' : 'bg-gray-300'}`}>
                                        <div className={`absolute top-1 w-3 h-3 rounded-full bg-white transition-transform ${moduleConfig[m.key] !== false ? 'left-6' : 'left-1'}`}></div>
                                    </div>
                                </div>
                                <p className="text-sm text-gray-500">{m.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Step 7: Team */}
            {step === 7 && (
                <div className="space-y-6">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Invite Your Team</h2>
                        <p className="text-gray-500">Send invites to managers and staff.</p>
                    </div>

                    <div className="space-y-2">
                        {invites.map((inv, i) => (
                             <div key={i} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100">
                                <div>
                                    <div className="font-medium text-blue-900">{inv.email}</div>
                                    <div className="text-xs text-blue-700 capitalize">{inv.roles?.[0]}</div>
                                </div>
                                <span className="text-xs bg-white px-2 py-1 rounded text-blue-600 border border-blue-200">Pending</span>
                             </div>
                        ))}
                    </div>

                    <div className="flex gap-2">
                        <input 
                            type="email" 
                            className="flex-1 rounded-lg border-gray-300 border px-3 py-2"
                            placeholder="colleague@restaurant.com"
                            value={newInvite.email}
                            onChange={e => setNewInvite({...newInvite, email: e.target.value})}
                        />
                        <select 
                            className="rounded-lg border-gray-300 border px-3 py-2"
                            value={newInvite.role}
                            onChange={e => setNewInvite({...newInvite, role: e.target.value})}
                        >
                            <option value="staff">Staff</option>
                            <option value="manager">Manager</option>
                            <option value="org_admin">Admin</option>
                        </select>
                        <button onClick={sendInvite} className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700">
                            Invite
                        </button>
                    </div>
                    {magicLinkResult && (
                        <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-sm break-all">
                            <p className="font-medium text-blue-800 mb-1">Magic link (share with finance lead):</p>
                            <code className="text-blue-900">{magicLinkResult}</code>
                        </div>
                    )}
                    {ENABLE_ONBOARDING_MVP && ingestEmail && (
                        <p className="text-sm text-gray-600">Ingest address: <span className="font-mono">{ingestEmail}</span></p>
                    )}
                </div>
            )}

            {/* Navigation Buttons */}
            <div className="absolute bottom-8 left-8 right-8 flex justify-between">
                <button 
                    onClick={() => setStep(s => Math.max(1, s - 1))}
                    disabled={step === 1}
                    className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50"
                >
                    <ChevronLeft className="w-4 h-4 mr-2" /> Back
                </button>

                {step < 6 ? (
                    <button 
                        onClick={async () => {
                            if (step === 2) await saveDepartments();
                            if (step === 3) await saveMenuConfig();
                            if (step === 6) await saveModules();
                            setStep(s => s + 1);
                        }}
                        className="flex items-center bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700"
                    >
                        {savingModules ? 'Saving...' : 'Next Step'} <ChevronRight className="w-4 h-4 ml-2" />
                    </button>
                ) : (
                    <button 
                        onClick={completeOnboarding}
                        className="flex items-center bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700"
                    >
                        Complete Setup <CheckCircle className="w-4 h-4 ml-2" />
                    </button>
                )}
            </div>
        </div>
      </div>
    </div>
  );
};
